import { useState, useEffect } from 'react';
import type { InterviewSession } from '../../types';
import { ConversationSettingsPanel } from '../conversation-settings/ConversationSettingsPanel';
import { buildConversationSettingsValues, prepareConversationSettingsSave, validateConversationSettings, type ConversationSettingsValues } from '../conversation-settings/settings-model';
import { useConversationSettingsAccess } from '../conversation-settings/useConversationSettingsAccess';

/** Persistence shape shared with the builder's settings object. */
export type SettingsForm = ConversationSettingsValues;

/** Props for {@link CampaignSettings}. */
export interface CampaignSettingsProps {
  /** Whether the settings panel is open. */
  open: boolean;
  /** Called when the panel should close. */
  onClose: () => void;
  /** The interview session to read current settings from. */
  session: InterviewSession;
  /** Called with the form data when the user saves. */
  onSave: (settings: SettingsForm) => Promise<void>;
  /** Whether a save is currently in progress. */
  saving: boolean;
  /** Error message to display (e.g. validation or API error). */
  error: string | null;
  /** Current number of responses already collected. */
  responseCount: number;
}

/** Adapt saved conversation state to the same controlled panel used by the builder. */
export function CampaignSettings({ open, onClose, session, onSave, saving, error, responseCount }: CampaignSettingsProps) {
  const [value, setValue] = useState(() => buildConversationSettingsValues(session));
  const { can, languagePolicy } = useConversationSettingsAccess();

  useEffect(() => {
    if (open) setValue(buildConversationSettingsValues(session));
  }, [open, session]);

  /** Cancel edits without changing the persisted conversation. */
  const handleClose = () => {
    setValue(buildConversationSettingsValues(session));
    onClose();
  };

  /** Recheck the shared guard before dispatching the host's persistence action. */
  const handleSave = () => {
    if (saving || validateConversationSettings(value, { fields: session.form_schema, responseCount, can }).invalid) return;
    void onSave(prepareConversationSettingsSave(value, can, languagePolicy));
  };

  return <ConversationSettingsPanel open={open} onClose={handleClose} value={value}
    onChange={patch => setValue(current => ({ ...current, ...patch }))}
    fields={session.form_schema} responseCount={responseCount} onSave={handleSave} saving={saving} error={error} />;
}
